import type { SafetyBadge } from "@hexling/core";
import type { ReactNode } from "react";

export type Tone = SafetyBadge["tone"];

const ICON: Record<Tone, string> = { danger: "☠", warning: "⚠", info: "ℹ", ok: "✓" };

export function Badge({ tone = "info", children }: { tone?: Tone; children: ReactNode }) {
  return (
    <span className={`hx-badge hx-badge--${tone}`}>
      <span aria-hidden="true">{ICON[tone]}</span>
      {children}
    </span>
  );
}

/** A wrapping row of safety badges. Renders nothing when there are none. */
export function SafetyBadges({ badges }: { badges: SafetyBadge[] }) {
  if (!badges.length) return null;
  return (
    <div className="hx-badges">
      {badges.map((b) => (
        <Badge key={b.label} tone={b.tone}>
          {b.label}
        </Badge>
      ))}
    </div>
  );
}

/**
 * The safety "warning label". Tone follows the most serious badge.
 * Always shown on items with safety data, even when everything is fine.
 */
export function SafetyLabel({
  badges,
  cautions = [],
  footnote,
}: {
  badges: SafetyBadge[];
  cautions?: string[];
  footnote?: ReactNode;
}) {
  const tone = badges.some((b) => b.tone === "danger")
    ? "danger"
    : badges.some((b) => b.tone === "warning")
      ? "warning"
      : "ok";
  const heading =
    tone === "danger"
      ? "Handle with care"
      : tone === "warning"
        ? "Use with care"
        : "No known safety concerns";
  return (
    <section className={`hx-safety hx-safety--${tone}`} aria-labelledby="hx-safety-heading">
      <h2 id="hx-safety-heading" className="hx-safety__heading">
        {heading}
      </h2>
      <SafetyBadges badges={badges} />
      {cautions.length > 0 && (
        <ul>
          {cautions.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
      )}
      {footnote && (
        <p className="hx-field__hint" style={{ marginTop: "0.6rem", marginBottom: 0 }}>
          {footnote}
        </p>
      )}
    </section>
  );
}
